import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import PrimaryButton from '../ui/buttons/primaryButton';
import { transferEvent } from '../../features/event/eventSlice';

const normalizeName = (value) => String(value || '').trim().toLowerCase();

/**
 * Moves an event into another workspace the signed-in user belongs to.
 *
 * A transfer changes where the event lives, not what is in it, so the dialog
 * warns before it acts and asks for the event's name as a guard against the
 * wrong event being moved.
 */
const TransferEventDialog = ({
  open,
  onClose,
  event,
  targets = [],
  sourceWorkspaceName = '',
  onTransferred
}) => {
  const dispatch = useDispatch();
  const [targetWorkspaceId, setTargetWorkspaceId] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const sortedTargets = useMemo(
    () => [...targets].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [targets]
  );

  useEffect(() => {
    if (!open) return;
    setTargetWorkspaceId('');
    setConfirmName('');
    setSubmitting(false);
  }, [open]);

  const eventName = String(event?.name || '').trim();
  const nameMatches = Boolean(eventName) && normalizeName(confirmName) === normalizeName(eventName);
  const canConfirm = Boolean(targetWorkspaceId) && nameMatches && !submitting;

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!canConfirm || !event?.id) return;
    setSubmitting(true);
    try {
      await dispatch(
        transferEvent({ eventId: event.id, workspaceId: targetWorkspaceId })
      ).unwrap();
      onTransferred?.();
    } catch (error) {
      // The thunk already raised the error alert; leave the dialog open so the
      // user can pick another workspace or try again.
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Transfer Event</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            select
            label="Transfer to workspace"
            value={targetWorkspaceId}
            onChange={(event) => setTargetWorkspaceId(event.target.value)}
            fullWidth
            disabled={submitting}>
            {sortedTargets.map((workspace) => (
              <MenuItem key={workspace.id} value={workspace.id}>
                {workspace.name || 'Untitled workspace'}
              </MenuItem>
            ))}
          </TextField>

          <Alert severity="warning">
            This moves <strong>{eventName}</strong> and all of its sections, teams, games,
            brackets and scores to the selected workspace. It will disappear from
            &quot;{sourceWorkspaceName || 'this workspace'}&quot;, and only an admin of the new
            workspace can move it back. Public score and embed links keep working.
          </Alert>

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Type the event name exactly to confirm.
            </Typography>
            <TextField
              label={`Type "${eventName}"`}
              value={confirmName}
              onChange={(event) => setConfirmName(event.target.value)}
              fullWidth
              autoComplete="off"
              disabled={submitting}
              error={Boolean(confirmName) && !nameMatches}
              helperText={
                confirmName && !nameMatches ? 'The name does not match the event.' : ' '
              }
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <PrimaryButton onClick={handleClose} disabled={submitting}>
          Cancel
        </PrimaryButton>
        <PrimaryButton
          color="error"
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}>
          {submitting ? 'Transferring…' : 'Transfer Event'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default TransferEventDialog;
